const TEMPLATE_CHROME_PATTERNS: RegExp[] = [
  /powered by boom/gi,
  /we found similar[^.!?]*[.!?]?/gi,
  /view (?:this )?(?:email|message) in (?:your )?browser/gi,
  /if you['’]?re having trouble viewing this email[^.!?]*[.!?]?/gi,
  /can['’]?t see this (?:email|message)\??/gi,
  /unsubscribe(?: from these emails)?/gi,
  /manage (?:your )?(?:email )?preferences/gi,
  /this (?:is an automated|email was sent)[^.!?]*[.!?]?/gi,
  /do not reply to this (?:email|message)/gi,
  /sent (?:with|via|from) boom/gi,
  /all rights reserved/gi,
  /©\s*\d{4}[^|]*/g
];

const HTML_ENTITIES: Record<string, string> = {
  "&nbsp;": " ",
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&#39;": "'",
  "&apos;": "'"
};

const decodeEntities = (value: string) =>
  value
    .replace(
      /&nbsp;|&amp;|&lt;|&gt;|&quot;|&#39;|&apos;/g,
      (match) => HTML_ENTITIES[match] ?? match
    )
    .replace(/&#(\d+);/g, (_, code: string) =>
      String.fromCharCode(Number(code))
    );

const stripHtml = (value: string) =>
  value
    .replace(/<(script|style)[^>]*>[\s\S]*?<\/\1>/gi, " ")
    .replace(/<[^<>]*>/g, " ");

const HTML_TAGS = new Set(
  "a abbr address article aside b blockquote body br button caption center code col colgroup dd div dl dt em font footer form h1 h2 h3 h4 h5 h6 header hr html i img input label li main nav ol p pre section small span strong sub sup table tbody td tfoot th thead tr u ul".split(
    " "
  )
);

const CSS_AT_RULE =
  /^@(?:media|font-face|import|charset|supports|keyframes|page)\b/i;

const isSelectorToken = (token: string) =>
  /^[*.#[:]/.test(token) ||
  HTML_TAGS.has(token.split(/[.#:[]/)[0].toLowerCase());

const isSelector = (prelude: string) =>
  CSS_AT_RULE.test(prelude) ||
  prelude
    .split(/[\s,>+~]+/)
    .filter(Boolean)
    .every(isSelectorToken);

const blockEnd = (value: string, open: number) => {
  let depth = 0;
  for (let index = open; index < value.length; index += 1) {
    if (value[index] === "{") depth += 1;
    else if (value[index] === "}") depth -= 1;
    if (depth === 0) return index + 1;
  }
  return -1;
};

const skipStatement = (value: string, index: number) => {
  const end = value.indexOf(";", index);
  return end < 0 ? value.length : end + 1;
};

const skipWhitespace = (value: string, from: number) => {
  let index = from;
  while (index < value.length && /\s/.test(value[index])) index += 1;
  return index;
};

const nextRuleEnd = (value: string, index: number) => {
  if (value.startsWith("@import", index) || value.startsWith("@charset", index))
    return skipStatement(value, index);
  const open = value.indexOf("{", index);
  const prelude = open < 0 ? "" : value.slice(index, open).trim();
  if (!prelude || prelude.length > 300 || !isSelector(prelude)) return -1;
  const end = blockEnd(value, open);
  if (end >= 0) return end;
  return CSS_AT_RULE.test(prelude) ? value.length : -1;
};

const stripLeadingCss = (value: string) => {
  let index = skipWhitespace(value, 0);
  let stripped = false;
  while (index < value.length) {
    const end = nextRuleEnd(value, index);
    if (end < 0) break;
    stripped = true;
    index = skipWhitespace(value, end);
  }
  return stripped ? value.slice(index) : value;
};

const stripUrls = (value: string) =>
  value.replace(/https?:\/\/\S+/gi, " ").replace(/\bwww\.\S+/gi, " ");

const collapseWhitespace = (value: string) => value.replace(/\s+/g, " ").trim();

const stripTemplateChrome = (value: string) =>
  TEMPLATE_CHROME_PATTERNS.reduce(
    (acc, pattern) => acc.replace(pattern, " "),
    value
  );

const stripLeadingPunctuation = (value: string) =>
  value.replace(/^[\s|·•\-–—:,.]+/, "");

export const stripMarkupForText = (value: string): string => {
  const hasTags = /<[a-z!/][^>]*>/i.test(value);
  const text = stripLeadingCss(
    hasTags ? decodeEntities(stripHtml(value)) : value
  );
  if (!hasTags && text === value) return value;
  return text
    .replace(/[ \t]+/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

export const buildMessageSnippet = (
  body: string | null | undefined,
  subject?: string | null
): string => {
  const cleaned = stripLeadingPunctuation(
    collapseWhitespace(
      stripTemplateChrome(
        stripUrls(stripLeadingCss(decodeEntities(stripHtml(body ?? ""))))
      )
    )
  );

  if (cleaned) return cleaned;

  return collapseWhitespace(decodeEntities(stripHtml(subject ?? "")));
};
